import { type CSSProperties, type ComponentProps, forwardRef, type ReactNode, isValidElement, useCallback, useEffect } from 'react';
import type { ThumbnailImage } from '@videojs/core';
import { CaptionsOffIcon, CaptionsOnIcon, CheckIcon, ChevronIcon, PauseIcon, PipEnterIcon, PipExitIcon, PlayIcon, RestartIcon, SpinnerIcon, VolumeHighIcon, VolumeLowIcon, VolumeOffIcon } from '@videojs/react/icons/minimal';
import { createPlayer, Container, usePlayer, BufferingIndicator, Controls, ErrorDialog, Gesture, Hotkey, Menu, MuteButton, PiPButton, useDoubleTapGesture, useHotkey, usePlaybackRateOptions, useTapGesture, Popover, SeekIndicator, Slider, StatusAnnouncer, StatusIndicator, Time, TimeSlider, Tooltip, VolumeIndicator, VolumeSlider } from '@videojs/react';
import { videoFeatures } from '@videojs/react/video';
import { isTimelinePlaybackToggleKey } from '../webPlaybackInputPolicy';
import "./player-skin.css";

const TOP_STATUS_ACTIONS = ['toggleSubtitles', 'toggleFullscreen', 'togglePictureInPicture'] as const;

const CENTER_STATUS_ACTIONS = ['togglePaused'] as const;

/**
 * Quality is auto, always — premium streamers don't ship rendition pickers.
 * Audio lives on the feed chip. What remains is speed, and it announces
 * itself: the trigger IS the current rate, no junk-drawer gear.
 */
function SpeedMenu(): ReactNode {
  const playbackRate = usePlaybackRateOptions();
  if (playbackRate?.state.availability !== 'available') return null;

  const currentRate = Number(playbackRate.value);
  const rateLabel = `${Number.isFinite(currentRate) && currentRate > 0 ? currentRate : 1}×`;

  return (
    <Menu.Root side="top" align="center">
      <Menu.Trigger
        aria-label={`Playback speed ${rateLabel}`}
        className="media-button--settings media-button--speed"
        render={<Button />}
      >
        <span className="media-speed-value">{rateLabel}</span>
      </Menu.Trigger>
      <Menu.Content className="media-popover media-menu media-menu--settings">
        <Menu.View className="media-menu__panel">
          <Menu.RadioGroup
            className="media-menu__group"
            value={playbackRate.value}
            onValueChange={playbackRate.setValue}
            aria-label="Playback rate"
          >
            {playbackRate.options.map((option) => (
              <Menu.RadioItem
                key={option.value}
                className="media-menu__item"
                value={option.value}
                disabled={option.disabled}
              >
                <span>{option.label}</span>
                <Menu.ItemIndicator
                  checked={option.value === playbackRate.value}
                  forceMount
                  className="media-menu__indicator"
                >
                  <CheckIcon className="media-icon" />
                </Menu.ItemIndicator>
              </Menu.RadioItem>
            ))}
          </Menu.RadioGroup>
        </Menu.View>
      </Menu.Content>
    </Menu.Root>
  );
}

// ================================================================
// Player
// ================================================================

const SEEK_TIME = 10;
const FALLBACK_FRAME_RATE = 30;

export const Player = createPlayer({ features: videoFeatures });

export interface PlayerSkinProps {
  /** The media element (HlsJsVideo plus its tracks). */
  children: ReactNode;
  thumbnails?: ThumbnailImage[];
  className?: string;
  style?: CSSProperties;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
  isFullscreen?: boolean;
  onFullscreen?: () => void;
  /** Route play/pause through the surface's de-click transport envelope. */
  onTogglePlayback?: () => void;
  /** Route every seek through the same transport envelope. */
  onSeek?: (seconds: number) => void;
  /** Start silencing before Video.js commits a timeline seek. */
  onSeekStart?: () => void;
  /** App-owned controls outside Video.js currently own pointer/focus. */
  controlsPinned?: boolean;
  /** Preserve the prior hidden state through an autonomous media promotion. */
  controlsSuppressed?: boolean;
  /** Real user intent cancels autonomous handoff suppression immediately. */
  onControlsRevealIntent?: () => void;
}

function PreviousDanceIcon(props: ComponentProps<'svg'>): ReactNode {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path fill="currentColor" d="M18 6.6a1 1 0 0 0-1.55-.83l-7.5 5.4a1 1 0 0 0 0 1.66l7.5 5.4A1 1 0 0 0 18 17.4V6.6z" />
      <path fill="currentColor" d="M7 5.5a1.25 1.25 0 0 0-2.5 0v13a1.25 1.25 0 0 0 2.5 0v-13z" />
    </svg>
  );
}

function NextDanceIcon(props: ComponentProps<'svg'>): ReactNode {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path fill="currentColor" d="M6 6.6a1 1 0 0 1 1.55-.83l7.5 5.4a1 1 0 0 1 0 1.66l-7.5 5.4A1 1 0 0 1 6 17.4V6.6z" />
      <path fill="currentColor" d="M17 5.5a1.25 1.25 0 0 1 2.5 0v13a1.25 1.25 0 0 1-2.5 0v-13z" />
    </svg>
  );
}

/** Same geometry as the native SeekBackward/SeekForward icons — one glyph
 * family across web and app chrome. */
function Seek10Icon({ backwards = false, ...props }: ComponentProps<'svg'> & { backwards?: boolean }): ReactNode {
  return (
    <svg viewBox="0 0 18 18" aria-hidden="true" {...props}>
      <g transform={backwards ? 'translate(18 0) scale(-1 1)' : undefined}>
        <path fill="currentColor" d="M9 1a7.98 7.98 0 0 1 6.132 2.867l1.441-1.44a.25.25 0 0 1 .427.177V6.75a.25.25 0 0 1-.25.25h-4.146a.25.25 0 0 1-.177-.427L13.71 5.29A5.99 5.99 0 0 0 9 3a6 6 0 0 0-4.242 10.242l-1.415 1.415A8 8 0 0 1 9 1" />
      </g>
      <text
        fill="currentColor"
        fontSize="6.5"
        fontWeight="600"
        textAnchor={backwards ? 'start' : 'end'}
        x={backwards ? 1 : 17}
        y={17}
      >
        10
      </text>
    </svg>
  );
}

/** App fullscreen glyphs (design/icons.tsx) — brackets out to enter, in to
 * exit — replacing the stock arrow icons so both chromes speak one language. */
function FullscreenEnterGlyph(props: ComponentProps<'svg'>): ReactNode {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path fill="currentColor" d="M4 9a1 1 0 0 0 2 0V6h3a1 1 0 0 0 0-2H5a1 1 0 0 0-1 1v4z" />
      <path fill="currentColor" d="M20 9a1 1 0 0 1-2 0V6h-3a1 1 0 0 1 0-2h4a1 1 0 0 1 1 1v4z" />
      <path fill="currentColor" d="M4 15a1 1 0 0 1 2 0v3h3a1 1 0 0 1 0 2H5a1 1 0 0 1-1-1v-4z" />
      <path fill="currentColor" d="M20 15a1 1 0 0 0-2 0v3h-3a1 1 0 0 0 0 2h4a1 1 0 0 0 1-1v-4z" />
    </svg>
  );
}

function FullscreenExitGlyph(props: ComponentProps<'svg'>): ReactNode {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path fill="currentColor" d="M9 4a1 1 0 0 0-2 0v3H4a1 1 0 0 0 0 2h4a1 1 0 0 0 1-1V4z" />
      <path fill="currentColor" d="M15 4a1 1 0 0 1 2 0v3h3a1 1 0 0 1 0 2h-4a1 1 0 0 1-1-1V4z" />
      <path fill="currentColor" d="M9 20a1 1 0 0 1-2 0v-3H4a1 1 0 0 1 0-2h4a1 1 0 0 1 1 1v4z" />
      <path fill="currentColor" d="M15 20a1 1 0 0 0 2 0v-3h3a1 1 0 0 0 0-2h-4a1 1 0 0 0-1 1v4z" />
    </svg>
  );
}

/**
 * Recital Master player skin — ejected from @videojs/react's Minimal video
 * skin (see docs how-to/customize-skins) and restructured: full-width
 * scrubber row above the transport row, sitting on a bottom scrim. Renders
 * inside Player.Provider, which VideoSurface owns.
 */
export function PlayerSkin({
  children,
  thumbnails,
  className,
  style,
  onPrevious,
  onNext,
  hasPrevious = false,
  hasNext = false,
  isFullscreen = false,
  onFullscreen,
  onTogglePlayback,
  onSeek,
  onSeekStart,
  controlsPinned = false,
  controlsSuppressed = false,
  onControlsRevealIntent
}: PlayerSkinProps): ReactNode {
  const player = Player.usePlayer();
  const requestSeek = useCallback(
    (seconds: number) => {
      if (onSeek) onSeek(seconds);
      else void player.seek(seconds);
    },
    [onSeek, player]
  );
  const requestRelativeSeek = useCallback(
    (delta: number) => {
      const duration = Number.isFinite(player.state.duration)
        ? player.state.duration
        : Number.POSITIVE_INFINITY;
      requestSeek(Math.min(duration, Math.max(0, player.state.currentTime + delta)));
    },
    [player, requestSeek]
  );

  useHotkey({
    keys: 'Space',
    onActivate: (event) => {
      event.preventDefault();
      onTogglePlayback?.();
    },
    disabled: !onTogglePlayback
  });
  useDoubleTapGesture(
    () => requestRelativeSeek(-SEEK_TIME),
    { region: 'left', disabled: !onSeek }
  );
  useDoubleTapGesture(
    () => requestRelativeSeek(SEEK_TIME),
    { region: 'right', disabled: !onSeek }
  );
  useTapGesture(
    (event) => {
      const interactive = event.composedPath().some(
        (node) =>
          node instanceof Element &&
          node.matches('button, input, [role="slider"], [role="menu"], [role="menuitem"]')
      );
      if (!interactive) onTogglePlayback?.();
    },
    { pointer: 'mouse', region: 'center', disabled: !onTogglePlayback }
  );
  useHotkey({
    keys: 'k',
    onActivate: (event) => {
      event.preventDefault();
      onTogglePlayback?.();
    },
    disabled: !onTogglePlayback
  });
  useEffect(() => {
    const handleQueueHotkey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }

      if (event.key === ',' || event.key === '.') {
        event.preventDefault();

        const state = player.state;
        const reportedFrameRate = state.activeVideoRendition?.frameRate;
        const frameRate =
          Number.isFinite(reportedFrameRate) && reportedFrameRate! > 0
            ? reportedFrameRate!
            : FALLBACK_FRAME_RATE;
        const direction = event.key === ',' ? -1 : 1;
        const duration = Number.isFinite(state.duration) && state.duration > 0
          ? state.duration
          : Number.POSITIVE_INFINITY;
        const targetTime = Math.min(
          duration,
          Math.max(0, state.currentTime + direction / frameRate)
        );

        if (!state.paused) onTogglePlayback?.();
        requestSeek(targetTime);
        return;
      }

      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        requestRelativeSeek(event.key === 'ArrowLeft' ? -SEEK_TIME / 2 : SEEK_TIME / 2);
        return;
      }

      if (event.key === 'Home' || event.key === 'End' || /^[0-9]$/.test(event.key)) {
        event.preventDefault();
        const duration = Number.isFinite(player.state.duration) ? player.state.duration : 0;
        const percent = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : Number(event.key) / 10;
        requestSeek(duration * percent);
        return;
      }

      if (event.repeat) return;

      if (event.key.toLowerCase() === 'j' && hasPrevious) {
        event.preventDefault();
        onPrevious?.();
      } else if (event.key.toLowerCase() === 'l' && hasNext) {
        event.preventDefault();
        onNext?.();
      }
    };

    window.addEventListener('keydown', handleQueueHotkey);
    return () => window.removeEventListener('keydown', handleQueueHotkey);
  }, [hasNext, hasPrevious, onNext, onPrevious, onTogglePlayback, player, requestRelativeSeek, requestSeek]);

  return (
    <Container
      className={`media-minimal-skin media-minimal-skin--video${controlsPinned ? ' media-minimal-skin--controls-pinned' : ''}${controlsSuppressed ? ' media-minimal-skin--controls-suppressed' : ''}${className ? ` ${className}` : ''}`}
      onFocusCapture={onControlsRevealIntent}
      onKeyDownCapture={onControlsRevealIntent}
      onPointerDownCapture={onControlsRevealIntent}
      onPointerMoveCapture={onControlsRevealIntent}
      style={style}
    >
      {children}


        <BufferingIndicator
          render={(props) => (
            <div {...props} className="media-buffering-indicator">
              <SpinnerIcon className="media-icon" />
            </div>
          )}
        />

        <ErrorDialog.Root>
          <ErrorDialog.Popup className="media-error">
            <div className="media-error__dialog">
              <div className="media-error__content">
                <ErrorDialog.Title className="media-error__title">Something went wrong.</ErrorDialog.Title>
                <ErrorDialog.Description className="media-error__description" />
              </div>
              <div className="media-error__actions">
                <ErrorDialog.Close className="media-button media-button--primary">OK</ErrorDialog.Close>
              </div>
            </div>
          </ErrorDialog.Popup>
        </ErrorDialog.Root>

        <Controls.Root className="media-controls">
          <Tooltip.Provider>
            <div className="media-timeline-row">
              <Time.Value
                type="current"
                className="media-time media-timeline-row__time media-timeline-row__time--current"
              />
              <TimeSlider.Root
                className="media-slider media-slider--time"
                onDragStart={onSeekStart}
                onKeyDownCapture={(event) => {
                  if (
                    isTimelinePlaybackToggleKey(event.key) &&
                    onTogglePlayback
                  ) {
                    event.preventDefault();
                    event.stopPropagation();
                    onTogglePlayback();
                    return;
                  }
                  if (
                    event.key === 'ArrowLeft' ||
                    event.key === 'ArrowRight' ||
                    event.key === 'ArrowUp' ||
                    event.key === 'ArrowDown' ||
                    event.key === 'PageUp' ||
                    event.key === 'PageDown' ||
                    event.key === 'Home' ||
                    event.key === 'End'
                  ) {
                    onSeekStart?.();
                  }
                }}
              >
                <TimeSlider.Track className="media-slider__track">
                  <TimeSlider.Fill className="media-slider__fill" />
                  <TimeSlider.Buffer className="media-slider__buffer" />
                </TimeSlider.Track>
                <TimeSlider.Thumb className="media-slider__thumb" />

                <div className="media-thumbnail media-slider__thumbnail">
                  <div className="media-thumbnail__image-wrapper">
                    <Slider.Thumbnail className="media-thumbnail__image" thumbnails={thumbnails} />
                  </div>
                  <TimeSlider.Value type="pointer" className="media-time media-thumbnail__time" />
                  <SpinnerIcon className="media-thumbnail__spinner media-icon" />
                </div>
                <TimeSlider.Preview className="media-slider__preview">
                  <TimeSlider.Value type="pointer" className="media-time media-slider__value" />
                </TimeSlider.Preview>
              </TimeSlider.Root>
              <Time.Value
                type="duration"
                className="media-time media-timeline-row__time media-timeline-row__time--duration"
              />
            </div>

            <div className="media-controls__row">
            <div className="media-button-group">
              <TransportPlayControl onToggle={onTogglePlayback} />

              <Tooltip.Root side="top">
                <Tooltip.Trigger
                  render={
                    <Button
                      aria-label="Previous dance"
                      className="media-button--previous"
                      disabled={!hasPrevious}
                      onClick={onPrevious}
                    >
                      <PreviousDanceIcon className="media-icon" />
                    </Button>
                  }
                />
                <Tooltip.Popup className="media-tooltip">
                  <Tooltip.Label>Previous dance</Tooltip.Label>
                  <Tooltip.Shortcut className="media-tooltip__kbd">J</Tooltip.Shortcut>
                </Tooltip.Popup>
              </Tooltip.Root>

              <Tooltip.Root side="top">
                <Tooltip.Trigger
                  render={
                    <Button
                      aria-label="Next dance"
                      className="media-button--next"
                      disabled={!hasNext}
                      onClick={onNext}
                    >
                      <NextDanceIcon className="media-icon" />
                    </Button>
                  }
                />
                <Tooltip.Popup className="media-tooltip">
                  <Tooltip.Label>Next dance</Tooltip.Label>
                  <Tooltip.Shortcut className="media-tooltip__kbd">L</Tooltip.Shortcut>
                </Tooltip.Popup>
              </Tooltip.Root>

              <Tooltip.Root side="top">
                <Tooltip.Trigger
                  render={
                    <Button
                      aria-label="Back 10 seconds"
                      className="media-button--seek-back"
                      onClick={() => requestRelativeSeek(-SEEK_TIME)}
                    >
                      <Seek10Icon backwards className="media-icon" />
                    </Button>
                  }
                />
                <Tooltip.Popup className="media-tooltip">
                  Back 10 seconds
                  <kbd className="media-tooltip__kbd">←</kbd>
                </Tooltip.Popup>
              </Tooltip.Root>

              <Tooltip.Root side="top">
                <Tooltip.Trigger
                  render={
                    <Button
                      aria-label="Forward 10 seconds"
                      className="media-button--seek-forward"
                      onClick={() => requestRelativeSeek(SEEK_TIME)}
                    >
                      <Seek10Icon className="media-icon" />
                    </Button>
                  }
                />
                <Tooltip.Popup className="media-tooltip">
                  Forward 10 seconds
                  <kbd className="media-tooltip__kbd">→</kbd>
                </Tooltip.Popup>
              </Tooltip.Root>
            </div>

            <div className="media-button-group media-button-group--end">
              <VolumePopover />

              <SpeedMenu />

              <Tooltip.Root side="top">
                <Tooltip.Trigger
                  render={
                    <PiPButton className="media-button--pip" render={<Button />}>
                      <PipEnterIcon className="media-icon media-icon--pip-enter" />
                      <PipExitIcon className="media-icon media-icon--pip-exit" />
                    </PiPButton>
                  }
                />
                <Tooltip.Popup className="media-tooltip">
                  <Tooltip.Label />
                  <Tooltip.Shortcut className="media-tooltip__kbd" />
                </Tooltip.Popup>
              </Tooltip.Root>

              <Tooltip.Root side="top">
                <Tooltip.Trigger
                  render={
                    <Button
                      aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                      className="media-button--fullscreen"
                      onClick={onFullscreen}
                    >
                      {isFullscreen ? (
                        <FullscreenExitGlyph className="media-icon" />
                      ) : (
                        <FullscreenEnterGlyph className="media-icon" />
                      )}
                    </Button>
                  }
                />
                {/* Plain-button trigger: Tooltip.Label reads from a Video.js
                    button and would render empty here, so label explicitly.
                    The `f` hotkey lives at the app level (watch screen). */}
                <Tooltip.Popup className="media-tooltip">
                  {isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                  <kbd className="media-tooltip__kbd">F</kbd>
                </Tooltip.Popup>
              </Tooltip.Root>
            </div>
            </div>
          </Tooltip.Provider>
        </Controls.Root>

        <div className="media-overlay" />

        {/* Hotkeys */}
        <Hotkey keys="m" action="toggleMuted" />
        <Hotkey keys="c" action="toggleSubtitles" />
        <Hotkey keys="i" action="togglePictureInPicture" />
        <Hotkey keys="ArrowUp" action="volumeStep" value={0.05} />
        <Hotkey keys="ArrowDown" action="volumeStep" value={-0.05} />
        <Hotkey keys=">" action="speedUp" />
        <Hotkey keys="<" action="speedDown" />

        {/* Gestures */}
        {!onTogglePlayback ? (
          <Gesture type="tap" action="togglePaused" pointer="mouse" region="center" />
        ) : null}
        <Gesture type="tap" action="toggleControls" pointer="touch" />

        {/* Input Feedback */}
        <StatusAnnouncer />
        <div className="media-input-feedback">
          <VolumeIndicator.Root className="media-input-feedback-island media-input-feedback-island--volume">
            <VolumeIndicator.Fill className="media-input-feedback-island__content">
              <VolumeHighIcon className="media-icon media-icon--volume-high" />
              <VolumeLowIcon className="media-icon media-icon--volume-low" />
              <VolumeOffIcon className="media-icon media-icon--volume-off" />
              <div className="media-input-feedback-island__progress" aria-hidden="true" />
              <VolumeIndicator.Value className="media-input-feedback-island__value" />
            </VolumeIndicator.Fill>
          </VolumeIndicator.Root>

          <StatusIndicator.Root
            actions={TOP_STATUS_ACTIONS}
            className="media-input-feedback-island media-input-feedback-island--status"
          >
            <div className="media-input-feedback-island__content">
              <CaptionsOnIcon className="media-icon media-icon--captions-on" />
              <CaptionsOffIcon className="media-icon media-icon--captions-off" />
              <FullscreenEnterGlyph className="media-icon media-icon--fullscreen-enter" />
              <FullscreenExitGlyph className="media-icon media-icon--fullscreen-exit" />
              <PipEnterIcon className="media-icon media-icon--pip-enter" />
              <PipExitIcon className="media-icon media-icon--pip-exit" />
              <StatusIndicator.Value className="media-input-feedback-island__value" />
            </div>
          </StatusIndicator.Root>

          <SeekIndicator.Root className="media-input-feedback-bubble">
            <ChevronIcon className="media-icon media-icon--seek" />
            <SeekIndicator.Value className="media-time" />
          </SeekIndicator.Root>

          <StatusIndicator.Root actions={CENTER_STATUS_ACTIONS} className="media-input-feedback-bubble">
            <PlayIcon className="media-icon media-icon--play" />
            <PauseIcon className="media-icon media-icon--pause" />
          </StatusIndicator.Root>
        </div>
    </Container>
  );
}

// ================================================================
// Components
// ================================================================

const Button = forwardRef<HTMLButtonElement, ComponentProps<'button'>>(function Button({ className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={`media-button media-button--subtle media-button--icon ${className ?? ''}`}
      {...props}
    />
  );
});

function TransportPlayControl({ onToggle }: { onToggle?: () => void }): ReactNode {
  const paused = usePlayer((state) => state.paused);
  const started = usePlayer((state) => state.started);
  const ended = usePlayer((state) => state.ended);
  const player = Player.usePlayer();
  const label = ended ? 'Replay' : paused ? 'Play' : 'Pause';

  return (
    <Tooltip.Root side="top">
      <Tooltip.Trigger
        render={
          <Button
            aria-label={label}
            className="media-button--play"
            data-ended={ended || undefined}
            data-paused={paused || undefined}
            data-started={started || undefined}
            onClick={() => {
              if (onToggle) onToggle();
              else if (paused) void player.play();
              else player.pause();
            }}
          >
            <RestartIcon className="media-icon media-icon--restart" />
            <PlayIcon className="media-icon media-icon--play" />
            <PauseIcon className="media-icon media-icon--pause" />
          </Button>
        }
      />
      <Tooltip.Popup className="media-tooltip">
        {label}
        <kbd className="media-tooltip__kbd">K</kbd>
      </Tooltip.Popup>
    </Tooltip.Root>
  );
}

function VolumePopover(): ReactNode {
  const volumeUnsupported = usePlayer((s) => s.volumeAvailability === 'unsupported');

  const muteButton = (
    <MuteButton className="media-button--mute" render={<Button />}>
      <VolumeOffIcon className="media-icon media-icon--volume-off" />
      <VolumeLowIcon className="media-icon media-icon--volume-low" />
      <VolumeHighIcon className="media-icon media-icon--volume-high" />
    </MuteButton>
  );

  if (volumeUnsupported) return muteButton;

  return (
    <Popover.Root openOnHover delay={0} closeDelay={100} side="top">
      <Popover.Trigger render={muteButton} />
      <Popover.Popup className="media-popover media-popover--volume">
        <VolumeSlider.Root className="media-slider" orientation="vertical" thumbAlignment="edge">
          <VolumeSlider.Track className="media-slider__track">
            <VolumeSlider.Fill className="media-slider__fill" />
          </VolumeSlider.Track>
          <VolumeSlider.Thumb className="media-slider__thumb media-slider__thumb--persistent" />
        </VolumeSlider.Root>
      </Popover.Popup>
    </Popover.Root>
  );
}

// ================================================================
// Utilities
// ================================================================
